import { useState } from "react";
import { SERVICES } from "@/lib/content";
import { Check, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Tabbed services showcase with animated image transitions.
 * Vertical tabs on desktop, horizontal pills on mobile.
 */
export default function ServicesTabs() {
  const [active, setActive] = useState(0);
  const svc = SERVICES[active];

  return (
    <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
      <div className="grid lg:grid-cols-12 gap-12 mb-12">
        <div className="lg:col-span-5">
          <div className="overline mb-4">Capabilities</div>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight text-charcoal leading-tight">Three pillars of a stronger workforce.</h2>
        </div>
        <div className="lg:col-span-6 lg:col-start-7 flex items-end">
          <p className="text-charcoal-soft text-base md:text-lg leading-relaxed">
            We built our firm on the disciplines that matter most. Practical compliance, sharp recruiting, and the operational knowledge to win government contracts.
          </p>
        </div>
      </div>

      {/* Mobile: horizontal pill tabs */}
      <div className="lg:hidden flex overflow-x-auto gap-2 mb-8 pb-2 -mx-6 px-6">
        {SERVICES.map((s, i) => (
          <button
            key={s.slug}
            data-testid={`service-tab-mobile-${s.slug}`}
            onClick={() => setActive(i)}
            className={`shrink-0 px-4 py-2 text-sm border transition-colors ${
              active === i ? "bg-charcoal text-gold border-charcoal" : "border-gray-300 text-charcoal"
            }`}
          >
            {s.tag}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-12 gap-0 border border-gray-200">
        {/* Desktop vertical tabs */}
        <div className="hidden lg:flex lg:col-span-4 flex-col border-r border-gray-200">
          {SERVICES.map((s, i) => (
            <button
              key={s.slug}
              data-testid={`service-tab-${s.slug}`}
              onClick={() => setActive(i)}
              className={`text-left p-8 border-b border-gray-200 last:border-b-0 transition-colors group ${
                active === i ? "bg-charcoal text-white" : "bg-white hover:bg-offwhite"
              }`}
            >
              <div className={`font-mono text-[10px] tracking-[0.28em] uppercase mb-3 ${active === i ? "text-gold" : "text-gold"}`}>
                0{i + 1} · {s.tag}
              </div>
              <div className={`font-heading text-xl font-medium ${active === i ? "text-white" : "text-charcoal"}`}>
                {s.label}
              </div>
              <div className={`mt-4 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.24em] ${
                active === i ? "text-gold" : "text-charcoal-soft group-hover:text-gold"
              }`}>
                {active === i ? "Selected" : "View"} <ArrowRight className="w-3 h-3" />
              </div>
            </button>
          ))}
        </div>

        {/* Content pane */}
        <div className="lg:col-span-8 bg-white">
          <AnimatePresence mode="wait">
            <motion.div
              key={svc.slug}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
              className="grid md:grid-cols-2 min-h-[520px]"
              data-testid={`service-panel-${svc.slug}`}
            >
              <div className="relative overflow-hidden bg-charcoal">
                <motion.img
                  key={svc.image}
                  src={svc.image}
                  alt={svc.label}
                  initial={{ scale: 1.08, opacity: 0.7 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 font-mono text-[10px] tracking-[0.24em] uppercase text-gold">
                  {svc.tag}
                </div>
              </div>
              <div className="p-8 md:p-10 flex flex-col">
                <h3 className="font-heading text-2xl md:text-3xl font-semibold text-charcoal leading-tight">{svc.label}</h3>
                <p className="mt-4 text-charcoal-soft leading-relaxed">{svc.summary}</p>
                <ul className="mt-6 space-y-3 flex-1">
                  {svc.items.slice(0, 4).map((it) => (
                    <li key={it.title} className="flex gap-3 items-start">
                      <Check className="w-4 h-4 text-gold shrink-0 mt-1" />
                      <span className="text-charcoal">{it.title}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/services"
                  data-testid={`service-cta-${svc.slug}`}
                  className="mt-8 inline-flex items-center gap-2 self-start bg-gold text-white px-5 py-3 text-sm font-medium hover:bg-gold-hover transition-colors"
                >
                  Explore this practice <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
